// Per-user, per-competition snapshot of `CompetitionDetail` so the student
// competition workspace can hydrate from cache and paint the program chrome
// instantly on subsequent visits, instead of flashing a full-page spinner
// every time the user routes back from `/student/competition/[id]/team`.
//
// Network fetch still runs in the background — the cache only seeds the
// first frame.
//
// Keying: `coravu.competition:{userId}:{competitionId}`. Per-user prefix
// so shared-browser sign-ins don't clobber each other.

import type { CompetitionDetail } from "../types/database";

const PREFIX = "coravu.competition:";

function key(userId: string, competitionId: string): string {
  return `${PREFIX}${userId}:${competitionId}`;
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

export function readCachedCompetition(
  userId: string,
  competitionId: string
): CompetitionDetail | undefined {
  const s = storage();
  if (!s) return undefined;
  try {
    const raw = s.getItem(key(userId, competitionId));
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as CompetitionDetail | null;
    if (!parsed || typeof parsed !== "object") return undefined;
    if (typeof parsed.id !== "string" || typeof parsed.name !== "string") {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

export function writeCachedCompetition(
  userId: string,
  competitionId: string,
  detail: CompetitionDetail
): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(key(userId, competitionId), JSON.stringify(detail));
  } catch {
    // quota / private mode — ignore
  }
}

export function clearCachedCompetition(userId: string, competitionId: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key(userId, competitionId));
  } catch {
    // ignore
  }
}
